'use client';

import {
  Drawer,
  DrawerTrigger,
  DrawerDescription,
  DrawerContent,
  DrawerTitle,
} from '@/src/components/ui/drawer';
import { IOrderTypes, ISchedules, IBranchScheduleDays, IBranchDetails } from '@/src/types/stores';
import { isRamadan } from '@/src/lib/utils/checkIfRamadan';
import { ConvertTimeTo12HourFormat } from '@/src/lib/utils/formatDay';
import { CalendarDays, CarFront, Clock9, Gift, Info } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { FC, memo, useMemo } from 'react';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import Container from '@/src/components/ui/container';

type Tprops = {
  schedules: ISchedules[];
  orderTypes: IOrderTypes[];
  isOpen: boolean;
  details: IBranchDetails;
};

type storeAvailabilityIcon = {
  icon: React.ReactNode;
};

const types: Record<number, storeAvailabilityIcon> = {
  1: { icon: <Clock9 /> },
  2: { icon: <CalendarDays /> },
  3: { icon: <Gift /> },
  4: { icon: <CarFront /> },
};

export const useTodaySchedule = (branchScheduleDays: IBranchScheduleDays[]) => {
  return useMemo(() => {
    const today = new Date().getDay();
    return branchScheduleDays.find((s) => s.day === today);
  }, [branchScheduleDays]);
};

const RenderScheduleDays = ({
  scheduleDays,
  isOpen,
}: {
  scheduleDays: ISchedules[];
  isOpen: boolean;
}) => {
  const t = useTranslations('stores');
  const normalSchedule = scheduleDays.filter((item) => item.scheduleType === 1);
  const ramdanSchedule = scheduleDays.filter((item) => item.scheduleType === 2);

  const currentScheduleDays = isRamadan() ? ramdanSchedule : normalSchedule;
  const todaySchedule = useTodaySchedule(currentScheduleDays[0]?.branchScheduleDays || []);

  return (
    <div className="styled-scroll my-3 flex gap-3 overflow-x-auto py-2">
      {currentScheduleDays?.map((item, index) => (
        <div key={index} className="styled-scroll flex items-center gap-2 rounded-lg text-center">
          {item.branchScheduleDays?.map((daySchedule, dayIndex) => (
            <div
              key={dayIndex}
              className={`${
                todaySchedule?.day !== daySchedule.day
                  ? 'bg-secondary'
                  : daySchedule.isClosed || !isOpen
                    ? 'bg-red-200/20 text-red-400'
                    : 'bg-success/20 text-success font-bold'
              } flex min-w-36 flex-col gap-3 rounded-lg px-2 py-4`}
            >
              <div>
                <CurrentLanguageText
                  arabicName={daySchedule.arabicDayString}
                  englishName={daySchedule.englishDayString}
                />
                <span className="mx-1">
                  {todaySchedule?.day !== daySchedule.day
                    ? daySchedule.isClosed && t('close')
                    : daySchedule.isClosed || !isOpen
                      ? t('close')
                      : t('open')}
                </span>
              </div>
              <span
                className={`${daySchedule.isClosed || todaySchedule?.day !== daySchedule.day ? 'bg-gray-5/50' : 'bg-success/20'} block h-[1px] w-full`}
              />
              {daySchedule.branchScheduleOpeningHours?.map((hours, hoursIndex) => (
                <p className="flex justify-center text-xs" key={hoursIndex}>
                  {ConvertTimeTo12HourFormat(hours.startHour)} -{' '}
                  {ConvertTimeTo12HourFormat(hours.endHour)}
                </p>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const StoreAvailability: FC<Tprops> = ({ orderTypes, schedules, isOpen, details }) => {
  const t = useTranslations('stores');

  return (
    <Drawer>
      <DrawerTrigger
        // onClick={() =>
        //   trackEvent(AmplitudeEvents.store_more_info_clicked, {
        //     storeID: details.store.id,
        //     storeName: details.store.englishName,
        //     branchID: details.id,
        //     branchName: details.englishName,
        //   })
        // }
        aria-label="Show Availability"
        className="cursor-pointer"
      >
        <Info className="text-gray-5" size="18" />
      </DrawerTrigger>
      <DrawerContent className="bg-white px-4 pb-4">
        <Container>
          <DrawerTitle className="mb-4">
            <span className="my-3 text-xl">{t('this-store-offers')}</span>
          </DrawerTitle>

          <section className="mb-4 flex items-center gap-3">
            {orderTypes?.map((item, index) => (
              <div key={index} className="text-primary flex items-center justify-center gap-1">
                <div className="align-middle">{types[item.type]?.icon}</div>
                <p className="mt-1 align-middle font-semibold">
                  <CurrentLanguageText
                    arabicName={item.arabicTypeString}
                    englishName={item.englishTypeString}
                  />
                </p>
              </div>
            ))}
          </section>

          <DrawerTitle className="my-3">{t('working-hours')} </DrawerTitle>

          <DrawerDescription />
          <RenderScheduleDays scheduleDays={schedules} isOpen={isOpen} />
        </Container>
      </DrawerContent>
    </Drawer>
  );
};

export default memo(StoreAvailability);
